import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import {
  AlertTriangle, Camera, Users, Activity,
  LayoutGrid, Video,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor,
  KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import DraggablePanel from '../components/DraggablePanel';
import { useApp } from '../contexts/AppContext';
// Dimuat malas: maplibre-gl besar, jangan membebani bundel awal (login dan dashboard).
const JakartaMap = lazy(() => import('../components/JakartaMap'));

// Compact stat card — dipakai di baris STATUS SISTEM.
const StatCard = ({ icon: Icon, label, value, sub, color, blink }) => (
  <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: 'var(--color-surface)', border: `1px solid ${color}22` }}>
    <div className="flex items-center justify-center rounded-lg shrink-0" style={{ width: 36, height: 36, background: color + '18' }}>
      <Icon size={17} color={color} />
    </div>
    <div className="min-w-0">
      <p className="text-[9px] font-bold uppercase tracking-widest truncate" style={{ color: 'var(--text-dim)' }}>{label}</p>
      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-black leading-none" style={{ color, fontFamily: 'IBM Plex Mono' }}>{value}</span>
        {blink && <span className="w-1.5 h-1.5 rounded-full animate-blink" style={{ background: color }} />}
      </div>
      <p className="text-[8.5px] mt-0.5 truncate" style={{ color: 'var(--text-mute)' }}>{sub}</p>
    </div>
  </div>
);

const PANEL_ORDER_KEY = 'horus.panelOrder.v3';
const DEFAULT_PANEL_ORDER = ['stats', 'mapneo', 'cctv'];

// Grid span per panel — stats melebar penuh, map lebih lebar dari CCTV.
const PANEL_SPAN = {
  stats:  'lg:col-span-5',
  mapneo: 'lg:col-span-3',
  cctv:   'lg:col-span-2',
};

const CommandCenter = ({ alerts, kameras, wantedCount, setPage }) => {
  const { t } = useApp();
  const [panelOrder, setPanelOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(PANEL_ORDER_KEY);
      if (!raw) return DEFAULT_PANEL_ORDER;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length !== DEFAULT_PANEL_ORDER.length) return DEFAULT_PANEL_ORDER;
      const valid = parsed.every(id => DEFAULT_PANEL_ORDER.includes(id))
                 && new Set(parsed).size === parsed.length;
      return valid ? parsed : DEFAULT_PANEL_ORDER;
    } catch {
      return DEFAULT_PANEL_ORDER;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(PANEL_ORDER_KEY, JSON.stringify(panelOrder)); } catch { /* ignore quota */ }
  }, [panelOrder]);

  const activeAlerts = useMemo(() => alerts.filter(a => !a.resolved), [alerts]);
  const onlineCams = useMemo(() => kameras.filter(k => k.status === 'ONLINE').length, [kameras]);

  const PANEL_DEFS = {
    stats:  { title: t('panel_stats'),  icon: Activity,   color: 'var(--color-cyan)' },
    mapneo: { title: t('panel_map'),    icon: LayoutGrid, color: '#7c4dff' },
    cctv:   { title: t('panel_cctv'),   icon: Video,      color: '#ff6b2b' },
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setPanelOrder(prev => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  const renderPanel = (id) => {
    switch (id) {
      case 'stats':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 p-3">
            <StatCard icon={Camera}        label={t('stat_cameras')}   value={kameras.length ? `${onlineCams}/${kameras.length}` : '0/0'} sub={t('sub_cameras')}   color="var(--color-cyan)" />
            <StatCard icon={Users}         label={t('stat_suspects')}  value={wantedCount}         sub={t('sub_suspects')}  color="#ff2d55" blink />
            <StatCard icon={AlertTriangle} label={t('stat_alerts')}    value={activeAlerts.length} sub={t('sub_alerts')}    color="#ff6b2b" blink={activeAlerts.length > 0} />
          </div>
        );

      case 'mapneo':
        return (
          <div className="p-3">
            <Suspense fallback={<div className="rounded-xl" style={{ height: 360, background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }} />}>
              <JakartaMap height={360} kameraData={kameras} insidentData={[]} />
            </Suspense>
          </div>
        );

      case 'cctv':
        return (
          <div className="grid grid-cols-2 gap-2.5 p-3">
            {kameras.slice(0, 4).map(cam => {
              const playlist = cam.playlist || [];
              const videoSrc = cam.stream_url || playlist[0]?.url || null;
              return (
                <div key={cam.id}
                  className="relative rounded-lg overflow-hidden cursor-pointer"
                  style={{ aspectRatio: '16/10', background: '#000', border: `1px solid ${cam.status === 'ONLINE' ? 'rgba(0,212,255,0.15)' : 'rgba(71,85,105,0.25)'}` }}
                  onClick={() => setPage('neovision')}>
                  {videoSrc ? (
                    <video src={videoSrc} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <div className="absolute inset-0 grid-bg opacity-40" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Camera size={15} color={cam.status === 'ONLINE' ? 'rgba(0,212,255,0.25)' : 'rgba(71,85,105,0.35)'} />
                      </div>
                      {cam.status === 'ONLINE' && <div className="absolute inset-0 scan-lines" />}
                    </>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1" style={{ background: 'rgba(2,6,14,0.88)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[8.5px] font-mono font-bold truncate" style={{ color: '#cfd8e3' }}>{cam.nama}</span>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: cam.status === 'ONLINE' ? 'var(--color-cyan)' : '#8898ad' }} />
                        <span className="text-[8px] font-mono font-bold" style={{ color: cam.status === 'ONLINE' ? 'var(--color-cyan)' : '#8898ad' }}>{cam.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

      default: return null;
    }
  };

  return (
    <div className="h-full overflow-y-auto c-scroll p-3" style={{ background: 'var(--color-bg)' }}>
      <div className="flex items-center gap-2 mb-2.5 px-1">
        <span style={{ fontSize: 9, color: 'var(--text-mute)' }}>⠿</span>
        <p className="text-[9px]" style={{ color: 'var(--text-mute)' }}>
          {t('drag_hint')} <span style={{ color: 'var(--text-dim)' }}>⠿</span> {t('drag_hint2')}
        </p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={panelOrder} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 items-start">
            {panelOrder.map(id => {
              const def = PANEL_DEFS[id];
              return (
                <DraggablePanel
                  key={id}
                  id={id}
                  title={def.title}
                  icon={def.icon}
                  accentColor={def.color}
                  className={PANEL_SPAN[id] || ''}
                >
                  {renderPanel(id)}
                </DraggablePanel>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default CommandCenter;
